import React, { useRef, useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import l from "components/util/l";
import DynamicTextArea from "components/util/helpers/fields/DynamicTextArea";
import SchemaForm from 'components/util/schema_form/SchemaForm';
import ExcelField from "components/util/helpers/fields/ExcelField";
import {
  attachFiles,
  getFileRequestOptions,
  isLocationAttachment,
  isMultipleAttachment,
  isScreenshotAttachment,
  onSelectAttachmentOption,
} from "./Conversation.lib.attachments";
import ConversationAttachmentMenu from "./Conversation.sub.attachmentMenu.view";
import ConversationCommentEditor from "./Conversation.sub.commentEditor.view";
import ConversationDropZone from "./Conversation.sub.dropZone.view";

const ConversationInput = ({
  attachmentOptions = [],
  flowId,
  formRef,
  lastBotMessage,
  chatSettings,
  isLoading,
  isStopShown,
  isPreview,
  takePhoto,
  takeScreenshot,
  onSendMessage,
  onClickRollbackExecution,
  onClickCancelChatAction,
}) => {

  const inputRef = useRef(null);
  const [selectSearch, setSelectSearch] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const disableOpenMessages = chatSettings?.disable_open_messages ?? false;

  const capturers = { takePhoto, takeScreenshot };

  // `auto_open` saves the click on the source a FILE field leads with; cancelling it leaves
  // the drop zone, so the field is still answered by hand. The explorer may not open: a
  // browser only shows a file picker right after a user gesture, and a question that
  // arrives later than that gets the drop zone alone.
  useEffect(() => {
    const schema = lastBotMessage?.settings?.schema;
    if (isPreview || schema?.type != "FILE" || !schema.auto_open) return;
    onSelectAttachmentOption(getFileRequestOptions(schema).sourceOption, formRef.current, capturers, flowId);
  }, [lastBotMessage?.id]);

  // Images and files pasted straight into the composer. A screenshot lives in the
  // clipboard, and making the user save it to disk before attaching it is a step nobody
  // takes — so the paste uploads it and leaves it attached to the next message.
  const onPasteFiles = (e, schema) => {

    const inputType = schema.type ?? "TEXT";
    if (inputType != "TEXT" && inputType != "TEXT_AREA" && inputType != "FILE") return;

    const files = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind == "file")
      .map(item => item.getAsFile())
      .filter(Boolean);

    if (files.length == 0) return;

    // Without this the browser also drops the file's name into the textarea as text.
    e.preventDefault();
    attachFiles(files, formRef.current, isMultipleAttachment(schema), flowId);

  }

  // A screenshot's comment stays editable until the message is sent: the user wrote it
  // while pointing at the screen, which is the worst moment to phrase something well.
  const onChangeAttachmentComment = (index, comment, values, setFieldValue) =>
    setFieldValue("attachments", (values.attachments || []).map((attachment, i) =>
      i == index ? { ...attachment, annotations: comment } : attachment));

  const onClickRemoveAttachment = (index, values, setFieldValue) =>
    setFieldValue("attachments", (values.attachments || []).filter((_, i) => i != index));

  // Customize enter key behavior in text area
  const handleEnterShift = (e, submitForm, inputType) => {
    if (!inputRef.current?.contains(e.target)) return;
    if (e.keyCode == 13 && !e.shiftKey && inputType != "TEXT_AREA") {
      e.preventDefault()
      submitForm()
    }
  }

  useEffect(() => {
    setSelectSearch("");
  }, [lastBotMessage]);

  return <>
    <div class="input-container">
      <Formik
        innerRef={formRef}
        initialValues={{}}
        onSubmit={(values, form) => {
          const schemaType = lastBotMessage?.settings?.schema?.type ?? "TEXT";
          const isMultipleSelect = schemaType == "SELECT" && (lastBotMessage?.settings?.schema?.multiple ?? false);

          form.setFieldValue('message', isMultipleSelect ? [] : '');
          form.setFieldValue('attachments', []);
          onSendMessage(values);
        }}>
        {({ values, setFieldValue, submitForm }) =>
          <Form
            ref={inputRef}
            onKeyDown={(e) => handleEnterShift(e, submitForm, lastBotMessage?.settings?.schema?.type ?? "TEXT")}
            onPaste={(e) => onPasteFiles(e, lastBotMessage?.settings?.schema ?? {})}
          >
            {
              function () {

                const templateName = lastBotMessage?.templateApplied?.[0]?.templateName || "default";
                const isDefaultTemplate = templateName == "default";
                let disableSend = false;

                const renderFields = [];
                const settings = lastBotMessage?.settings || {};
                const supported = settings?.supported ?? false;

                const schema = settings?.schema ?? {};
                const form = settings?.form ?? {};

                const type = schema?.type ?? "TEXT";
                const label = schema?.label ?? l("Escribe un mensaje%PA004");
                const placeholder = isDefaultTemplate ? l("¿En qué te puedo ayudar?%PA005") : (schema?.placeholder ?? l("Ingresa un valor%GA092"));
                const multipleFiles = schema?.multiple_files ?? false;
                const options = settings?.options ?? [];
                const openText = schema?.open_text ?? false;
                const assetType = schema?.asset_type ?? "file";
                const assetValue = schema?.asset_value;
                const multiple = schema?.multiple ?? false;
                const selectedValues = Array.isArray(values["message"]) ? values["message"] : [];
                const { browseOption, sourceOption } = getFileRequestOptions(schema);
                // The source a FILE field leads with stays at hand next to send, for when
                // its auto-open was cancelled.
                const isFileSourceShown = type == "FILE" && sourceOption.id != browseOption.id;

                if (disableOpenMessages && templateName == "default") {
                  disableSend = true;
                }
                else if (type == "NO_INPUT") {
                  disableSend = true;
                }
                else if (type == "TEXT" || type == "TEXT_AREA" || isLoading) {
                  renderFields.push(
                    <DynamicTextArea
                      id="chat-prompt-text" className={`position-relative ${isDefaultTemplate ? "chat-controls" : ""}`}
                      value={values["message"]}
                      onChange={(value) => setFieldValue("message", value)}
                      minRows={type == "TEXT_AREA" ? 2 : 1}
                      maxRows={type == "TEXT_AREA" ? 8 : 5}
                      placeholder={placeholder} />);
                }
                else if (type == "SELECT") {
                  if (openText && !multiple)
                    renderFields.push(
                      <Field
                        id="chat-prompt-select"
                        name="message"
                        type="text"
                        autocomplete="off"
                        placeholder={l("Ingresa un valor%GA092")}
                      />);
                  renderFields.push(
                    <div className={`input-options overflow-auto ${openText ? "" : "fixed-select"}
                    animate__animated animate__fadeIn animate__faster`}>

                      {options.length > 5 &&
                        <div className="options-search d-flex align-items-center mb-2">
                          <input type="text"
                            value={selectSearch}
                            onChange={e => setSelectSearch(e.target.value)} />
                          <i className="fas fa-search mr-2" />
                          {selectSearch != "" &&
                            <i className="fas fa-times"
                              onClick={() => setSelectSearch("")} />
                          }
                        </div>}
                      {options
                        .filter(option => ((option.label ?? option.value ?? "").toLowerCase()).includes(selectSearch.toLowerCase()))
                        .map((option, index) => {

                          const optionValue = option.value ?? option.label;
                          const optionLabel = option.label ?? option.value;
                          const isSelected = multiple && selectedValues.includes(optionValue);

                          return (
                            <label
                              key={index}
                              onClick={() => {

                                if (multiple) {

                                  const hasValue = selectedValues.includes(optionValue);
                                  let nextValues = [];

                                  if (hasValue) {
                                    nextValues = selectedValues.filter((value) => value != optionValue);
                                  }
                                  else {
                                    nextValues = [...selectedValues, optionValue];
                                  }

                                  setFieldValue("message", nextValues);
                                }
                                else {
                                  onSendMessage({ message: optionValue }, { setFieldValue, });
                                }

                              }}
                              className={`message suggestion text-truncate ${isSelected ? "selected" : ""}`}>
                              {option.icon && <i className={`${option.icon} mr-2`} />}
                              {optionLabel}
                            </label>
                          );

                        })}
                    </div>
                  );
                }
                else if (type == "DATE") {
                  renderFields.push(<Field id="chat-prompt-date" name="message" type="date" />)
                }
                else if (type == "DATE_TIME") {
                  renderFields.push(<Field id="chat-prompt-date-local" name="message" type="datetime-local" />);
                }
                else if (type == "FILE") {
                  // A single-file field is answered once something is attached; removing
                  // the chip brings the zone back.
                  const isFileAttached = (values["attachments"]?.length ?? 0) > 0;
                  if (multipleFiles || !isFileAttached)
                    renderFields.push(
                      <ConversationDropZone
                        browseOption={browseOption}
                        isDisabled={isPreview || values["attachments_loading"] == true}
                        onSelectOption={(option) =>
                          onSelectAttachmentOption(option, formRef.current, capturers, flowId)}
                      />)
                }
                else if (type == "ASSET") {
                  if (assetType == "image") renderFields.push(<img src={assetValue} alt={label} />)
                  else if (assetType == "file") renderFields.push(
                    <a href={assetValue} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                      <i className="fas fa-file-alt mr-2"></i>{l("Ver archivo%PA006")}
                    </a>
                  )
                }
                else if (type == "EXCEL") {
                  renderFields.push(<ExcelField
                    defaultSelectedSheet={schema?.excel_sheet}
                    defaultOffset={schema?.excel_offset}
                    defaultDataOffset={schema?.excel_data_offset}
                    referenceColumns={schema?.excel_structure}
                    ignoreColumns={true}
                    isSingleImport={true}
                    showAdvancedOptions={false}
                    showPreview={false}
                    overrideColumns={true}
                    onLoadFile={(data) => {

                      const { file, sheet, offset, rows, columns } = data;
                      const filteredColumns = schema?.excel_structure || [];

                      let hasSameColumns = true;

                      for (let i = 0; i < Math.max(columns.length, filteredColumns.length); i++) {

                        const importedColumn = columns[i];
                        const filteredColumn = filteredColumns[i];

                        if (!importedColumn || !filteredColumn) {
                          hasSameColumns = false;
                          break;
                        }

                        if (importedColumn?.label != filteredColumn?.label) {
                          hasSameColumns = false;
                          break;
                        }

                      }

                      setFieldValue("message", {
                        file,
                        sheet,
                        offset,
                        rows,
                        columns: schema?.excel_structure,
                        error: !hasSameColumns
                      });

                    }}
                    setLoading={(loading) => setFieldValue("attachments_loading", loading)}
                  />);
                }
                else if (!supported || schema.type == "FORM") {

                  let isSchema = schema?.type == "FORM";
                  let schemaFields = isSchema ? schema.fields : [{ ...schema, label: null, placeholder: null, show_if: [] }];
                  renderFields.push(
                    <div id="chat-prompt-form"
                      className="animate__animated animate__fadeInUp animate__faster">
                      <SchemaForm
                        schema={schemaFields}
                        appId={form?.app_id}
                        actionId={form?.action_id}
                        flowId={form?.flow_id}
                        nodeId={form?.node_id}
                        executionId={form?.execution_id}
                        isReadOnly={false}
                        showSideSteps={false}
                        onChange={(values) => setFieldValue("message", values)}
                        submitComponent={({ values }) => <div />}
                      />
                    </div>)
                }

                  const hasArrayMessage = Array.isArray(values["message"]) && values["message"].length > 0;
                  const hasScalarMessage = !!values["message"];
                  const hasMessage = hasArrayMessage || hasScalarMessage;
                  const hasAttachments = (values["attachments"]?.length ?? 0) > 0;
                  const isAttachmentsLoading = values["attachments_loading"] == true;
                  const isMessageInvalid = values["message"]?.error;
                  const isFormSchema = schema.type == "FORM";
                  const isSendDisabled = isLoading || isMessageInvalid || (!hasMessage && !isFormSchema && (!hasAttachments || isAttachmentsLoading));

                const attachments = Array.isArray(values["attachments"]) ? values["attachments"] : [];

                return <>

                  {/* What is already attached to the next message. A screenshot is the odd
                      one out: its file name says nothing, so the chip shows the capture
                      next to the comment — still editable, because it was written while
                      pointing at the screen. */}
                  {attachments.length > 0 &&
                    <div className="input-attachments d-flex flex-wrap align-items-center mb-2">
                      {attachments.map((attachment, index) =>
                        <div key={attachment.id ?? index}
                          className="attachment-chip d-flex align-items-center mr-2 mb-1 px-2 py-1"
                          title={attachment.annotations || getChipLabel(attachment)}>

                          {isLocationAttachment(attachment)
                            ? <i className="fas fa-location-dot mr-2" />
                            : isImageFile(attachment)
                              ? <img src={attachment.url} alt={getChipLabel(attachment)}
                                className="attachment-thumb mr-2" />
                              : <i className="fas fa-paperclip mr-2" />}

                          <span className="small text-truncate">
                            {attachment.annotations || getChipLabel(attachment)}
                          </span>

                          {isScreenshotAttachment(attachment) &&
                            <button type="button"
                              className="btn btn-link btn-sm p-0 ml-2"
                              title={l("Editar comentario%CH028")}
                              onClick={() => setEditingIndex(index)}>
                              <i className="fas fa-pen" />
                            </button>}

                          <button type="button"
                            className="btn btn-link btn-sm p-0 ml-2"
                            title={l("Quitar%VF096")}
                            onClick={() => onClickRemoveAttachment(index, values, setFieldValue)}>
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      )}
                    </div>}

                  {editingIndex != null && attachments[editingIndex] &&
                    <ConversationCommentEditor
                      imageUrl={attachments[editingIndex].url}
                      comment={attachments[editingIndex].annotations || ""}
                      onChangeComment={(comment) =>
                        onChangeAttachmentComment(editingIndex, comment, values, setFieldValue)}
                      onClose={() => setEditingIndex(null)}
                      onSave={() => setEditingIndex(null)}
                    />}

                  <div className="animate__animated animate__fadeIn animate__faster">{renderFields}</div>

                  <div className="input-actions">

                    {isFileSourceShown &&
                      <button type="button"
                        className={`fas ${sourceOption.icon} attach`}
                        title={sourceOption.label}
                        disabled={isPreview || isAttachmentsLoading}
                        onClick={() => onSelectAttachmentOption(sourceOption, formRef.current, capturers, flowId)}
                      />}

                    {isStopShown &&
                      <div className="btn-group dropup">
                        <button
                          type="button"
                          className="fas fa-stop cancel"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                        </button>
                        <div class="dropdown-menu py-2">
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => {
                              setFieldValue('message', '');
                              setFieldValue('attachments', []);
                              onClickCancelChatAction();
                            }}>
                            <i className="fas fa-stop dropdown-item-icon my-0 py-0" />
                            {l("Detener proceso%PA312")}
                          </button>
                          <button
                            type="button"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => {
                              setFieldValue('message', '');
                              setFieldValue('attachments', []);
                              onClickRollbackExecution();
                            }
                            }>
                            <i className="fas fa-undo dropdown-item-icon my-0 py-0" />
                            {l("Regresar un paso%PA313")}
                          </button>
                        </div>
                      </div>
                    }

                    {type == "TEXT" &&
                      <ConversationAttachmentMenu
                        count={attachments.length}
                        options={attachmentOptions}
                        isDisabled={isAttachmentsLoading}
                        onSelectOption={(option) =>
                          onSelectAttachmentOption(option, formRef.current, capturers, flowId)}
                      />
                    }

                    {
                      !disableSend &&
                      <button type={isPreview ? "button" : "submit"}
                        className={"send fas " +
                          (values["attachments_loading"] == true ? "fa-spinner " : "fa-circle-arrow-right ")}
                        disabled={isSendDisabled} />
                    }

                  </div>

                </>

              }()
            }

          </Form>}
      </Formik>
    </div>
  </>;
}

// What the chip says it is: a picked file goes by its name, and a location has none.
// What the chip calls it. A picked file goes by its name; a location and a screenshot
// have none worth showing — the screenshot's generated file name least of all.
const getChipLabel = (attachment) => {
  if (isLocationAttachment(attachment)) return l("Ubicación%CH023");
  if (isScreenshotAttachment(attachment)) return l("Captura de pantalla%CH027");
  return getFileName(attachment);
}

// Uploaded files keep the name they were stored under, prefixed with an id — the same
// unwrapping the attachment bubble does.
const getFileName = (file) => {
  const name = file?.name ?? "";
  return name.includes("_") ? name.split("_").slice(1).join("_") : name;
}

const isImageFile = (file) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file?.url ?? file?.name ?? "");

export default ConversationInput;
